import { useApiClient, useIDTagging, useTranslation } from '@msdk/common'
import { ControlValidationType, MeasurementUnitModel, StringFieldDefinition, StringValue } from '@msdk/definitions'
import { FieldProps, nodeHasFlag, useFieldSeverity, useFieldValue, ValidationMessages } from '@msdk/forms'
import { Autocomplete, FormField, TextField } from '@msdk/ui'
import React, { SyntheticEvent, useEffect, useState } from 'react'

interface Props extends FieldProps {
  label: string
  filters?: { sameFamilyOf?: string | null }
}

interface MuOption {
  label: string
  id: string
}

export function MuSelectorAutocomplete({ node, onChange, label, filters }: Props) {
  const fieldDef = node.value.fieldDefinition as StringFieldDefinition
  const client = useApiClient()
  const hasMandatoryFlag = nodeHasFlag(node, ControlValidationType.Mandatory)

  const [value, setValue] = useFieldValue(node, onChange)
  const [valueToShow, setValueToShow] = useState<string | null>(null)
  const [supportedMeasurementUnits, setSupportedMeasurementUnits] = useState<MeasurementUnitModel[]>([])

  const t = useTranslation()
  const idTaggingEnabled = useIDTagging()

  useEffect(() => {
    const fetch = async () => {
      const res = await client.read<MeasurementUnitModel[]>('CMPS/api/metadata/measurementunit')

      const sortedAndTranslatedMeasurementUnits = (res as MeasurementUnitModel[]).sort((a, b) => {
        if (a.key < b.key) return -1
        return a.key > b.key ? 1 : 0
      })

      setSupportedMeasurementUnits(sortedAndTranslatedMeasurementUnits)
    }
    fetch().catch(console.error)
  }, [])

  useEffect(() => {
    if (value && supportedMeasurementUnits?.length > 0) {
      const mu = supportedMeasurementUnits.find((x) => x.key === value.value)

      if (mu) {
        setValueToShow(t(mu.idml, mu.description))
      } else {
        setValueToShow(value.value as string)
      }
    } else {
      setValueToShow(null)
    }
  }, [value, supportedMeasurementUnits, idTaggingEnabled])

  const updateValue = (newValue: StringValue | null) => {
    node.value.isDirty = true
    setValue(newValue)
  }

  const onFieldChange = (_e: SyntheticEvent<Element, Event>, newValue: MuOption | null) => {
    const fieldValue = newValue ? new StringValue(newValue.id) : null
    updateValue(fieldValue)
  }

  const onInputFieldChange = (_e: SyntheticEvent<Element, Event>, newValue: string | null) => {
    const fieldValue = newValue ? new StringValue(newValue) : null
    const mu = supportedMeasurementUnits.find((x) => x.description === newValue)
    if (mu) {
      updateValue(new StringValue(mu.key))
    } else {
      updateValue(fieldValue)
    }
  }

  const isReadOnly = node.value.state === 'Readonly'
  const severity = useFieldSeverity(fieldDef, node)

  const measurementUnit = supportedMeasurementUnits.find((x) => x.key === filters?.sameFamilyOf)
  const conversionFamily = measurementUnit?.conversionFamily

  const filtered = conversionFamily
    ? supportedMeasurementUnits.filter((x) => x.conversionFamily === conversionFamily)
    : supportedMeasurementUnits

  return (
    <FormField size="small" severity={severity} readOnly={isReadOnly} disabled={isReadOnly}>
      <Autocomplete
        size="small"
        value={value ? value.value : null}
        onChange={onFieldChange}
        inputValue={valueToShow ? valueToShow : ''}
        onInputChange={onInputFieldChange}
        freeSolo
        options={filtered.map((mu) => {
          return {
            label: mu.description,
            id: mu.key,
          }
        })}
        disabled={node.value.state === 'Readonly'}
        renderInput={(params) => (
          <TextField required={hasMandatoryFlag} {...params} label={label} disabled={isReadOnly} />
        )}
      />
      <ValidationMessages messages={node.value.validationMessages} fieldDef={fieldDef} node={node} />
    </FormField>
  )
}
